import React, { useRef, useState, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import {
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/solid';

import {
  Cluster,
  Stack
} from '../EveryLayout'
import {
  FormButton,
  Spinner
} from '../Page';
import { Modal } from '../Misc/Modal';

const NewsletterSignup = ({ children }) => {

  const data = useStaticQuery(graphql`
    query {
      image: file(name: {eq: "Abler Nordic India-27"}) {
        childImageSharp {
          gatsbyImageData(width: 800, aspectRatio: 0.75)
        }
      }
    }
  `);

  const [error, setError] = useState(false);
  const [message, setMessage] = useState('');
  const [waiting, setWaiting] = useState(false);

  // setShowModal(false) to dismiss modal
  const [showModal, setShowModal] = useState(false);
  const emailField = useRef(null);
  const marketingPermissionField = useRef(null);

  async function signup(e) {
    e.preventDefault()
    setWaiting(true);

    const form = e.target;
    const body = JSON.stringify({
      email: form.email.value,
      marketing_permissions: [
        // Get the ID by inspecting a record returned when you send a POST to:
        // https://{{MAILCHIMP_SERVER_PREFIX}}.api.mailchimp.com/3.0/lists/{{audienceId}}/members
        {
          'marketing_permission_id': '8c3fe979cf',
          'enabled': form.marketing_permission_email.checked,
        }
      ],
    });

    const doRequest = async () => {
      const response = await fetch('/api/newsletter', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body,
        })
        .then((res) => {
          setWaiting(false);
          return res.json();
        });
      setMessage(response.message);
      setError(!response.ok);

      if (emailField && emailField.current) {
        if (response.resetEmail !== undefined && response.resetEmail) {
          // clear email field
          emailField.current.value = '';
        }
      }

    }

    doRequest();
  }

  const modalOpenState = (state) => {
    setShowModal(state);
    setMessage('');
    setError(false);
  }

  const timerHandle = useRef(null);

  const [ showSpinner, setShowSpinner ] = useState(false);

  useEffect(() => {
    if (waiting) {
      timerHandle.current = setTimeout(() => setShowSpinner(true), 500);
    } else {
      clearTimeout(timerHandle.current);
      setShowSpinner(false);
    }
  }, [waiting]);


  return (
    <>
      <div onClick={() => setShowModal(true)}>
        {children}
      </div>

      <Modal
        open={showModal}
        handleSetOpen={modalOpenState}
        title=""
        image={data.image}
        className="lg:max-w-4xl xl:max-w-5xl"
      >
        <Stack style={{ '--space': 'var(--space-8)' }}>
          <div className="prose prose-custom">
            <p>
              Sign up for news and updates from Abler Nordic
            </p>
          </div>

          <form onSubmit={signup} method="POST" action="/api/newsletter">
            <Stack>
              <Cluster className="flex-row">
                <div className="">
                  <label
                    htmlFor="email"
                    className="text-[1.125rem] sm:text-[1.3125rem]"
                  >
                    Email:
                  </label>
                </div>
                <div className="basis-[100%] sm:basis-0 flex-1">
                  <input
                    type="text"
                    id="email"
                    ref={emailField}
                    aria-describedby={ error ? 'subscription-error' : '' }
                    aria-invalid={error}
                    className="w-full sm:w-full !bg-white p-1 border-0 border-b !border-soft-black text-[1.125rem] sm:text-[1.3125rem]"
                  />

                </div>
              </Cluster>
              <div>
                <fieldset className="mt-2 p-4 bg-white border border-gray-300">
                  <legend className="text-xs">Marketing permissions</legend>
                  <p className="text-xxs leading-[1.25]">Please select all the ways you would like to hear from Abler Nordic:</p>

                  <div className="flex items-center gap-2 mt-3 mb-2">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          id="marketing_permission_email"
                          ref={marketingPermissionField}
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="offers" className="text-xxs">
                        Email
                      </label>
                    </div>
                  </div>

                  <p className="mb-4 text-xxs text-gray-700">
                    You can unsubscribe at any time by clicking the link in the footer of our emails. Please read our <a href="/privacy-policy/">Privacy Policy</a>.
                  </p>
                  <p className="mb-4 text-xxs text-gray-700">
                    We use Mailchimp as our marketing platform. By subscribing, you acknowledge that your information will be transferred to Mailchimp for processing. Read more about <a rel="nofollow noreferrer" href="https://mailchimp.com/legal/terms">Mailchimp's privacy practices</a>.
                  </p>
                </fieldset>
              </div>

              {error && (
                <div className="prose prose-custom">
                  <div
                    id="subscription-error"
                    aria-live="polite"
                    className="text-xs text-red inline-flex"
                  >
                    <span className="mr-1"><ExclamationCircleIcon className="flex-shrink-0 h-6 w-6 text-red" aria-hidden="true" /></span>{message}
                  </div>
                </div>
              )}

              {(!error && !!message) && (
                <div className="prose prose-custom">
                  <div
                    aria-live="polite"
                    className="text-xs text-dark-green inline-flex"
                  >
                    <span className="mr-1"><CheckCircleIcon className="flex-shrink-0 h-8 w-8 text-light-green" aria-hidden="true" /></span>{message}
                  </div>
                </div>
              )}

              <Cluster>
                <FormButton>
                  Subscribe
                </FormButton>
                <div>
                  {showSpinner
                    && (
                      <Spinner />
                    )
                  }
                </div>
              </Cluster>
            </Stack>
          </form>
        </Stack>
      </Modal>

    </>
  );
}

export default NewsletterSignup;
